import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import ImageAnnotationComponentV2, {
  ImageAnnotationComponentV2Handle,
} from '../../annotation/ImageAnnotationComponentV2'
import { Content } from '../../../types/models/content'

import {
  useCreateAnnotationMutation,
  useDeleteAnnotationMutation,
} from '../../../services/annotationApi'
import { useViewContentQuery, useGetContentImageQuery } from '../../../services/contentApi'
import { useGetModelQuery, useRunRealtimeInferenceMutation } from '../../../services/modelsApi'
import { PredictionMetadata } from '../../../types/models/prediction'
import {
  clearAllBoundingBoxes,
  selectSelectedContentId,
  selectEditingContent,
  setEditingContent,
  setSelectedContentId,
  clearInitialBoundingBoxes,
  setInitialBoundingBoxes,
  setRejectedBoundingBoxes,
  setAcceptedBoundingBoxes,
  mergeAllBoundingBoxes,
  setSelectedBoundingBox,
  selectEditingTagIds,
  clearEditingTagIds,
  setEditingTagIds,
  clearRedoStack,
  clearUndoStack,
} from '../../../store/slices/annotationSlice'
import { CreateBBoxAnnotationReq } from '../../../types/api/requests'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { RootState } from '../../../store'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { useGetTagsQuery } from '../../../services/tagApi'

import { selectSelectedModelId, setSelectedModelId } from '../../../store/slices/modelSlice'
import SelectModelTreeModal from '../../modals/SelectModelTreeModal/SelectModelTreeModal'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import TagsPanel from './components/TagsPanel'
import AnnotationRightPanel from './components/AnnotationRightPanel'
import CanvasTools from './components/CanvasTools'
import ReviewScaffold from '../../annotation/review/ReviewScaffold'
import { useAnnotationShortcuts } from '../../annotation/hooks/useAnnotationShortcuts'
import { useAnnotationIterationShortcuts } from '../../annotation/hooks/useAnnotationIterationShortcuts'
import { annotationToUIBoxes } from '../../annotation/utils/boxTransforms'
import useAnnotationsReview from './hooks/useAnnotationsReview'
import { Tag } from '../../../types/models/tag'

type ActiveTab = 'content' | 'annotated'

interface AnnotationsReviewProps {
  content: Content
  onBack?: () => void
  onNext?: () => void
  onPrevious?: () => void
  filteredAnnotatedContent: Content[]
  filteredUnannotatedContent: Content[]
  activeTab: ActiveTab
}

const AnnotationsReview: React.FC<AnnotationsReviewProps> = ({
  content,
  onBack,
  onNext,
  onPrevious,
  filteredAnnotatedContent,
  filteredUnannotatedContent,
  activeTab,
}) => {
  const dispatch = useAppDispatch()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const editingTagIds = useAppSelector(selectEditingTagIds)
  const selectedModelId = useAppSelector(selectSelectedModelId)

  const { data: selectedInferenceModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    pollingInterval: 60000,
  })

  const [createAnnotation] = useCreateAnnotationMutation()
  const [inferenceLoading, setInferenceLoading] = useState(false)
  const [runInference] = useRunRealtimeInferenceMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const [showSelectModelModal, setShowSelectModelModal] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const selectedPredictions = useAppSelector(
    (state: RootState) => state.prediction.selectedPredictions
  )
  const selectedContentId = useAppSelector(selectSelectedContentId)

  const editingContent = useAppSelector(selectEditingContent)

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Centralized review state/selectors via hook
  const {
    // state
    // selectedSnippetIndex, // not used directly once focused index is derived from visible list
    setSelectedSnippetIndex,
    hoveredBoxIndex,
    setHoveredBoxIndex,
    activeRightTab,
    setActiveRightTab,
    expandedTags,
    setExpandedTags,
    // selectors
    allBoundingBoxes,
    selectedBBoxClassIdx,
    visibleBoxes,
    // uniqueTags,
    // helpers
    handleTagSelect,
    toggleTagExpansion,
    selectNeighborInVisibleList,
    getCurrentSelectedBox,
    getFocusedIndexForVisibleList,
  } = useAnnotationsReview({ tags: tags?.tags })

  // New: selection by tag state
  const [selectedTagIdsForExport, setSelectedTagIdsForExport] = useState<Set<string>>(new Set())
  const handleToggleTagSelection = useCallback((tagId: string, checked: boolean) => {
    setSelectedTagIdsForExport((prev) => {
      const next = new Set(prev)
      if (checked) next.add(tagId)
      else next.delete(tagId)
      return next
    })
  }, [])

  // Reset export selection state when content changes (next/previous image)
  useEffect(() => {
    setSelectedTagIdsForExport(new Set())
  }, [content?.id])

  const { data: contentData } = useViewContentQuery(
    {
      ContentID: selectedContentId || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    {
      skip: !selectedContentId,
    }
  )

  const [deleteAnnotation] = useDeleteAnnotationMutation()

  const [showBBoxes, setShowBBoxes] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1)
  const annotationRef = useRef<ImageAnnotationComponentV2Handle>(null)
  const [showConfirmNullAnnotation, setShowConfirmNullAnnotation] = useState(false)
  const [showConfirmUnannotated, setShowConfirmUnannotated] = useState(false)

  // Add cache-busting state for image refreshing
  const [imageCacheTimestamp, setImageCacheTimestamp] = useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  // Add content image query
  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentData?.id || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    { skip: !contentData?.id || !selectedProjectId || !project?.datasetid }
  )
  // Handler for clicking on a Current tag (delegate to hook)
  const handleCurrentTagClick = useCallback(
    (box: AnnotationDataBoundingBox) => {
      handleTagSelect(box)
    },
    [handleTagSelect]
  )

  useEffect(() => {
    dispatch(clearUndoStack())
    dispatch(clearRedoStack())
    dispatch(setSelectedBoundingBox(undefined))

    if (project?.annotation_type === 'classification') {
      const existingTagIds =
        editingContent?.annotation && editingContent.annotation.length > 0
          ? editingContent.annotation[0]?.tagids || []
          : []
      dispatch(setEditingTagIds(existingTagIds))
      const bBoxes = existingTagIds?.map((tagid: string, index: number) => {
        return {
          name: tags?.tags?.find((t: Tag) => t.id === tagid)?.id,
          xmin: 0,
          ymin: 0,
          xmax: 0,
          ymax: 0,
          confidence: 0,
          class_index: index,
          points: [0, 0, 0, 0, 0, 0, 0, 0],
          ai_labeled: false,
        } as AnnotationDataBoundingBox
      })
      dispatch(mergeAllBoundingBoxes(bBoxes ? [...bBoxes] : []))
      dispatch(setInitialBoundingBoxes(bBoxes ? [...bBoxes] : []))
      dispatch(setRejectedBoundingBoxes([]))
      dispatch(setAcceptedBoundingBoxes([]))
    } else {
      if (!project?.annotation_type) return
      const boxes = annotationToUIBoxes(
        [
          ...(editingContent?.annotation?.[0]?.metadata?.bounding_boxes || []),
          ...(editingContent?.annotation?.[0]?.metadata?.oriented_bounding_boxes || []),
        ],
        project.annotation_type
      )
      dispatch(mergeAllBoundingBoxes(boxes))
      dispatch(setInitialBoundingBoxes(boxes))
      dispatch(setRejectedBoundingBoxes([]))
      dispatch(setAcceptedBoundingBoxes([]))
    }
  }, [editingContent, tags, project?.annotation_type, dispatch])

  const annotationTagIds = useMemo(() => {
    return project?.annotation_type === 'classification'
      ? editingTagIds
      : Array.from(new Set(allBoundingBoxes.map((a) => a.name)))
  }, [project?.annotation_type, editingTagIds, allBoundingBoxes])

  const hasAnyAnnotations = useMemo(() => {
    if (project?.annotation_type === 'classification') {
      return (editingTagIds?.length || 0) > 0
    }
    return allBoundingBoxes.length > 0
  }, [project?.annotation_type, editingTagIds, allBoundingBoxes.length])

  const handleAcceptCurrentAnnotations = useCallback(
    async (turnIntoContent = false) => {
      if (isProcessing) return
      setIsProcessing(true)
      if (editingContent) {
        try {
          if (turnIntoContent) {
            try {
              deleteAnnotation({
                id: editingContent?.annotation?.[0]?.id || '',
                dataset_id: project?.datasetid || '',
                project_id: selectedProjectId || '',
              })
            } catch (error: unknown) {
              const msg = (error as { data?: { message?: string } } | null)?.data?.message
              setErrorMessage(msg || 'Failed to create content')
              setShowError(true)
            }
          } else {
            // Create annotation request CreateAnnotationReq
            const annoToCreate: CreateBBoxAnnotationReq = {
              content_id: selectedContentId || '',
              dataset_id: project?.datasetid || '',
              metadata:
                project?.annotation_type === 'bounding_box'
                  ? { bounding_boxes: allBoundingBoxes }
                  : project?.annotation_type === 'oriented_bounding_box'
                  ? { oriented_bounding_boxes: allBoundingBoxes }
                  : undefined,
              project_id: selectedProjectId || '',
              tag_id: annotationTagIds,
              model_id: selectedModelId || '',
              ai_labeled: false,
              image_width: contentData?.width,
              image_height: contentData?.height,
            }
            await createAnnotation(annoToCreate)
          }

          // Reset all states
          dispatch(clearAllBoundingBoxes())
          dispatch(clearInitialBoundingBoxes())
          dispatch(clearEditingTagIds())
          setSelectedSnippetIndex(undefined)
          dispatch(setSelectedBoundingBox(undefined))
          setExpandedTags(new Set())

          // If there are no more images to annotate, go back to the gallery
          const currentFilteredContent =
            activeTab === 'annotated' ? filteredAnnotatedContent : filteredUnannotatedContent
          if (!currentFilteredContent || currentFilteredContent.length <= 1) {
            onBack?.()
            return
          }

          onNext?.()
        } catch (error: unknown) {
          const msg = (error as { data?: { message?: string } } | null)?.data?.message
          setErrorMessage(msg || 'Failed to create annotation')
          setShowError(true)
        } finally {
          setIsProcessing(false)
        }
      } else {
        for (const prediction of selectedPredictions) {
          try {
            await createAnnotation({
              content_id: prediction.contentid,
              dataset_id: selectedInferenceModel?.datasetid || '',
              project_id: selectedInferenceModel?.projectid || '',
              tag_id: prediction.predictions.map((p: PredictionMetadata) => p.tagid),
              model_id: selectedModelId || '',
              ai_labeled: false,
              image_width: contentData?.width,
              image_height: contentData?.height,
              metadata:
                project?.annotation_type === 'bounding_box'
                  ? {
                      bounding_boxes: prediction.predictions.flatMap((p: PredictionMetadata) =>
                        p.bounding_boxes ? Object.values(p.bounding_boxes) : []
                      ),
                    }
                  : project?.annotation_type === 'oriented_bounding_box'
                  ? {
                      oriented_bounding_boxes: prediction.predictions.flatMap(
                        (p: PredictionMetadata) =>
                          p.oriented_bounding_boxes ? Object.values(p.oriented_bounding_boxes) : []
                      ),
                    }
                  : undefined,
            }).unwrap()
          } catch (error: unknown) {
            const msg = (error as { data?: { message?: string } } | null)?.data?.message
            setErrorMessage(msg || 'An unexpected error occurred')
            setShowError(true)
          }
        }

        setExpandedTags(new Set())
        setIsProcessing(false)
      }
    },
    [
      editingContent,
      dispatch,
      activeTab,
      filteredAnnotatedContent,
      filteredUnannotatedContent,
      onNext,
      deleteAnnotation,
      project?.datasetid,
      project?.annotation_type,
      selectedProjectId,
      selectedContentId,
      allBoundingBoxes,
      annotationTagIds,
      selectedModelId,
      contentData?.width,
      contentData?.height,
      createAnnotation,
      onBack,
      selectedPredictions,
      selectedInferenceModel?.datasetid,
      selectedInferenceModel?.projectid,
      setSelectedSnippetIndex,
      setExpandedTags,
      isProcessing,
    ]
  )

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel * 1.2, 5)
    setZoomLevel(newZoom)
    if (annotationRef.current) {
      annotationRef.current.setZoom(newZoom)
    }
  }

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel / 1.2, 0.2)
    setZoomLevel(newZoom)
    if (annotationRef.current) {
      annotationRef.current.setZoom(newZoom)
    }
  }

  const handleResetZoom = () => {
    annotationRef.current?.recenterImage?.()
  }

  // Centralized annotation shortcuts
  useAnnotationShortcuts({
    isActive: !isProcessing,
    onSubmit: () => {
      if (hasAnyAnnotations) {
        handleAcceptCurrentAnnotations(false)
      } else {
        if (activeTab === 'content') setShowConfirmNullAnnotation(true)
        else setShowConfirmUnannotated(true)
      }
    },
    onNextImage: onNext,
    onPrevImage: onPrevious,
    undo: () => annotationRef.current?.undo?.(),
    redo: () => annotationRef.current?.redo?.(),
  })

  // Keyboard iteration within current visible list (snippets/tags)
  useAnnotationIterationShortcuts({
    isActive: !isProcessing,
    selectNeighborInVisibleList,
    getCurrentSelectedBox,
    onDeleteSelected: () => handleDelete(),
    onZoomToBox: (box) => annotationRef.current?.zoomToBBox(box),
  })

  const handleUndo = () => {
    annotationRef.current?.undo()
  }

  const handleRedo = () => {
    annotationRef.current?.redo()
  }

  const handleDelete = () => {
    setSelectedSnippetIndex(undefined)
    if (allBoundingBoxes.filter((box) => expandedTags.has(box.name)).length === 1) {
      setExpandedTags(new Set())
    }
    annotationRef.current?.delete()
    // Clear selection after deletion is complete
    dispatch(setSelectedBoundingBox(undefined))
  }

  const handleSnippetSelect = (box: AnnotationDataBoundingBox) => {
    annotationRef.current?.zoomToBBox(box)
    const idx = allBoundingBoxes.findIndex((b) => b.class_index === box.class_index)
    setSelectedSnippetIndex(idx >= 0 ? idx : undefined)
    dispatch(setSelectedBoundingBox(box))
  }

  // const handleSnippetSelect = useCallback(
  //   (index: number) => {
  //     const list = visibleBoundingBoxes
  //     const box = list[index]
  //     if (!box) {
  //       setSelectedSnippetIndex(undefined)
  //       dispatch(setSelectedBoundingBox(undefined))
  //       return
  //     }
  //     setSelectedSnippetIndex(index)
  //     dispatch(setSelectedBoundingBox(box))
  //   },
  //   [dispatch, visibleBoundingBoxes]
  // )

  const onTagExpand = (tagId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    toggleTagExpansion(tagId)
  }

  const handleRemoveAllTags = useCallback(
    (tagId: string) => {
      try {
        if (annotationRef.current) {
          annotationRef.current.removeAllTags(tagId)
        }
        dispatch(setSelectedBoundingBox(undefined))
        setSelectedSnippetIndex(undefined)
      } catch (error) {
        console.error('Error removing tag:', error)
      }
    },
    [dispatch, setSelectedSnippetIndex]
  )

  const handleRunInference = async () => {
    if (!selectedContentId || !selectedInferenceModel || !contentData || !imageUrl) return

    try {
      setInferenceLoading(true)
      const formData = new FormData()
      const blob = await fetch(imageUrl).then((r) => r.blob())
      formData.append('files', blob, 'image.jpg')

      // Keep track of manually drawn boxes (boxes without confidence values)
      // Preserve the original class_index for manual boxes
      const manualBoxes = allBoundingBoxes
        .filter((box) => !box.confidence)
        .map((box) => ({
          ...box,
          class_index: box.class_index, // Keep original class_index
        }))

      // Find the highest class_index among manual boxes
      const maxManualIndex = Math.max(...manualBoxes.map((box) => box.class_index), -1)

      // Clear all boxes first
      dispatch(clearAllBoundingBoxes())
      dispatch(clearInitialBoundingBoxes())

      // Restore manual boxes with their original indices
      if (manualBoxes.length > 0) {
        dispatch(mergeAllBoundingBoxes(manualBoxes))
        dispatch(setInitialBoundingBoxes(manualBoxes))
      }

      const result = await runInference({
        id: selectedInferenceModel.id,
        data: formData,
        heatmap: true,
      }).unwrap()

      if (result && result[0]?.predictions) {
        if (result[0].predictions.length === 0) {
          setErrorMessage(
            'No objects were detected in the image. You may want to try a different model or check if the image is suitable for detection.'
          )
          setShowError(true)
        } else {
          // Convert predictions to bounding boxes, starting indices after the highest manual box index
          const startIndex = maxManualIndex + 1

          const newBoxes = result[0].predictions
            .map((pred: PredictionMetadata, index: number) => {
              // Find the tag ID by matching the class name
              const matchingTag = tags?.tags?.find((tag: Tag) => tag.name === pred.class_name)

              // Get bounding box coordinates
              const bboxData = pred.bounding_boxes || pred.oriented_bounding_boxes

              if (!bboxData) return null

              // Transform inference coordinates - real-time inference typically returns pixel coordinates
              // but we need to handle both cases for consistency
              let bbox: AnnotationDataBoundingBox

              if (bboxData.points && bboxData.points.length === 8) {
                // Case 1: Points exist (check if they need coordinate transformation)
                const maxX = Math.max(
                  bboxData.points[0],
                  bboxData.points[2],
                  bboxData.points[4],
                  bboxData.points[6]
                )
                const maxY = Math.max(
                  bboxData.points[1],
                  bboxData.points[3],
                  bboxData.points[5],
                  bboxData.points[7]
                )

                let finalPoints: number[]

                if (maxX <= 1 && maxY <= 1) {
                  // Points are normalized (0-1), convert to pixel coordinates
                  finalPoints = [
                    bboxData.points[0] * contentData.width, // x1
                    bboxData.points[1] * contentData.height, // y1
                    bboxData.points[2] * contentData.width, // x2
                    bboxData.points[3] * contentData.height, // y2
                    bboxData.points[4] * contentData.width, // x3
                    bboxData.points[5] * contentData.height, // y3
                    bboxData.points[6] * contentData.width, // x4
                    bboxData.points[7] * contentData.height, // y4
                  ]
                } else {
                  // Points are already in pixel coordinates
                  finalPoints = [...bboxData.points]
                }

                // Calculate min/max from pixel coordinates
                const xValues = [finalPoints[0], finalPoints[2], finalPoints[4], finalPoints[6]]
                const yValues = [finalPoints[1], finalPoints[3], finalPoints[5], finalPoints[7]]
                const xmin = Math.min(...xValues)
                const ymin = Math.min(...yValues)
                const xmax = Math.max(...xValues)
                const ymax = Math.max(...yValues)

                bbox = {
                  name: matchingTag?.id || pred.tagid || pred.class_name,
                  class_name: pred.class_name,
                  points: finalPoints,
                  ai_labeled: true, // Mark as AI-generated
                  xmin,
                  ymin,
                  xmax,
                  ymax,
                  confidence: pred.confidence,
                  class_index: startIndex + index,
                } as AnnotationDataBoundingBox
              } else if (
                bboxData.xmin !== undefined &&
                bboxData.xmax !== undefined &&
                bboxData.ymin !== undefined &&
                bboxData.ymax !== undefined
              ) {
                // Case 2: Only min/max exists
                let xmin: number, ymin: number, xmax: number, ymax: number

                if (bboxData.xmax <= 1 && bboxData.ymax <= 1) {
                  // Coordinates are normalized (0-1), convert to pixel coordinates
                  xmin = bboxData.xmin * contentData.width
                  ymin = bboxData.ymin * contentData.height
                  xmax = bboxData.xmax * contentData.width
                  ymax = bboxData.ymax * contentData.height
                } else {
                  // Coordinates are already in pixel format
                  xmin = bboxData.xmin
                  ymin = bboxData.ymin
                  xmax = bboxData.xmax
                  ymax = bboxData.ymax
                }

                // Create points array from min/max (in pixel coordinates)
                // Points format: [x1, y1, x2, y2, x3, y3, x4, y4] - clockwise from top-left
                const points = [
                  xmin,
                  ymin, // top-left
                  xmax,
                  ymin, // top-right
                  xmax,
                  ymax, // bottom-right
                  xmin,
                  ymax, // bottom-left
                ]

                bbox = {
                  name: matchingTag?.id || pred.tagid || pred.class_name,
                  class_name: pred.class_name,
                  points, // Include points array for consistency
                  ai_labeled: true, // Mark as AI-generated
                  xmin,
                  ymin,
                  xmax,
                  ymax,
                  confidence: pred.confidence,
                  class_index: startIndex + index,
                } as AnnotationDataBoundingBox
              } else {
                return null
              }

              return bbox
            })
            .filter(
              (box: AnnotationDataBoundingBox | null): box is AnnotationDataBoundingBox =>
                box !== null
            )

          // Combine manual boxes with new inference boxes
          const combinedBoxes = [...manualBoxes, ...newBoxes]

          // Update the boxes
          dispatch(setInitialBoundingBoxes(combinedBoxes))
          dispatch(mergeAllBoundingBoxes(combinedBoxes))

          // Update editing tag IDs with unique tags from all boxes
          const uniqueTagIds = Array.from(
            new Set(combinedBoxes.map((box: AnnotationDataBoundingBox) => box.name))
          )
          dispatch(setEditingTagIds(uniqueTagIds))
        }
      } else {
        setErrorMessage(
          'No predictions were returned from the model. You may want to try a different model or check if the image is suitable for detection.'
        )
        setShowError(true)
      }
    } catch (error: unknown) {
      const err = error as { status?: number; data?: { message?: string } } | undefined
      if (err?.status === 500) {
        setErrorMessage('Server error running inference. Please try again.')
      } else if (err?.data?.message) {
        setErrorMessage(`Error: ${err.data.message}`)
      } else {
        setErrorMessage('Error running inference. Please try again.')
      }
      setShowError(true)
    } finally {
      setInferenceLoading(false)
    }
  }

  // uniqueTags provided by hook

  useEffect(() => {
    dispatch(setSelectedContentId(content.id))
    dispatch(setEditingContent(content))
    dispatch(clearAllBoundingBoxes())
  }, [content, dispatch])

  return (
    <div
      className="d-flex flex-column flex-fill h-100 pb-3"
      style={{ minHeight: 0, background: '#1a1a1a', overflow: 'visible' }}
    >
      <ReviewScaffold
        left={
          <CanvasTools
            onBackToGallery={() => onBack?.()}
            onPrevious={
              onPrevious ||
              (() => {
                // Default empty function for previous navigation
              })
            }
            onNext={
              onNext ||
              (() => {
                // Default empty function for next navigation
              })
            }
            onSubmit={() => handleAcceptCurrentAnnotations(false)}
            hasAnnotations={hasAnyAnnotations}
            showConfirmDialog={
              activeTab === 'content'
                ? () => setShowConfirmNullAnnotation(true)
                : () => setShowConfirmUnannotated(true)
            }
            activeTab={activeTab}
            onModelSelect={() => {
              setShowSelectModelModal(true)
            }}
            onRunInference={handleRunInference}
            inferenceLoading={inferenceLoading}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetZoom={handleResetZoom}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onDelete={handleDelete}
            showDelete={true}
            showBBoxes={showBBoxes}
            onToggleBBoxes={() => setShowBBoxes(!showBBoxes)}
            datasetName={contentData?.metadata?.datasource}
          />
        }
        center={
          editingContent ? (
            <div className="position-relative w-100">
              {isProcessing && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
                  style={{ zIndex: 10 }}
                >
                  <div className="text-center">
                    <Spinner animation="border" variant="light" />
                    <div className="mt-2 text-light">Submitting annotations…</div>
                  </div>
                </div>
              )}
              <ImageAnnotationComponentV2
                ref={annotationRef}
                contentId={selectedContentId}
                useBBoxes={
                  project?.annotation_type === 'bounding_box' ||
                  project?.annotation_type === 'oriented_bounding_box'
                }
                focusedBBoxIndex={getFocusedIndexForVisibleList()}
                // setFocusedBBoxIndex={setSelectedSnippetIndex}
                showBBoxes={showBBoxes}
                filteredBoxes={visibleBoxes}
                zoomLevel={zoomLevel}
                onZoomChange={setZoomLevel}
                hoveredBoxIndex={hoveredBoxIndex}
                showConfidence={true}
                labelMode={'class'}
                isEditable={true}
                selectOnFocus={true}
              />
            </div>
          ) : null
        }
        right={
          editingContent ? (
            <AnnotationRightPanel
              key={selectedContentId || content.id}
              contentId={selectedContentId || ''}
              activeTab={activeRightTab as 'snippets' | 'tags'}
              onSelectTab={(k) => setActiveRightTab(k === 'snippets' ? 'snippets' : 'tags')}
              boxes={allBoundingBoxes}
              selectedBBoxClassIndex={selectedBBoxClassIdx}
              onSnippetSelect={handleSnippetSelect}
              hoveredBoxIndex={hoveredBoxIndex}
              onHover={setHoveredBoxIndex}
              onToggleTagSelection={handleToggleTagSelection}
              selectedTagIds={selectedTagIdsForExport}
              tagsContent={
                <TagsPanel
                  tags={tags?.tags || []}
                  onRemoveAllTags={handleRemoveAllTags}
                  filteredBoundingBoxes={allBoundingBoxes}
                  selectedBBoxClassIndex={selectedBBoxClassIdx}
                  onTagSelect={handleCurrentTagClick}
                  expandedTags={expandedTags}
                  onTagExpand={onTagExpand}
                  showBoxCount={project?.annotation_type === 'bounding_box'}
                  onDelete={handleDelete}
                  ai_labeled={editingContent?.annotation?.[0]?.ai_labeled}
                />
              }
            />
          ) : null
        }
      />

      <ConfirmDialog
        show={showConfirmNullAnnotation}
        onHide={() => setShowConfirmNullAnnotation(false)}
        onConfirmRight={() => handleAcceptCurrentAnnotations(false)}
        title={'Make Null Annotation'}
        message={'Make this image a NULL ANNOTATION.'}
        variant="primary"
        confirmRightText={'Make Null (Enter)'}
        cancelText="Cancel (Esc)"
      />

      <ConfirmDialog
        show={showConfirmUnannotated}
        onHide={() => setShowConfirmUnannotated(false)}
        onConfirmRight={() => handleAcceptCurrentAnnotations(true)}
        title={'Remove from Annotations'}
        message={'Make this image unannotated and move to Content.'}
        variant="primary"
        confirmRightText={'Move to Content (Enter)'}
        cancelText="Cancel (Esc)"
      />
      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />

      <SelectModelTreeModal
        show={showSelectModelModal}
        onHide={() => setShowSelectModelModal(false)}
        onModelSelect={(model) => {
          dispatch(
            setSelectedModelId({
              modelId: model.id,
              projectId: selectedProjectId || '',
            })
          )
          setShowSelectModelModal(false)
        }}
        selectedModelId={selectedModelId ?? null}
      />
    </div>
  )
}

export default AnnotationsReview
